<script setup lang="ts">
const props = withDefaults(defineProps<{
  direction?: 'horizontal' | 'vertical'
  minSize?: number
  /** Caps the NEXT sibling's size (the one being dragged smaller/larger from the right/bottom).
   *  Optional — omit for an unbounded panel, as the file-explorer drawer does today. */
  maxSize?: number
  /** A 1px divider that takes no layout width: the visible line IS the handle's whole box, and a
   *  wider invisible hit area sits over it. For a handle between two surfaces that must touch
   *  (the Documentos panel and the sheet); the default 8px box leaves a strip of whatever is
   *  behind it between them. Horizontal only. */
  flush?: boolean
}>(), { direction: 'horizontal', minSize: 40, flush: false })

const emit = defineEmits<{ resize: [prev: number, next: number] }>()
const el = ref<HTMLElement | null>(null)
const dragging = ref(false)
const isH = computed(() => props.direction === 'horizontal')

function onPointerDown(e: PointerEvent) {
  const prev = el.value!.previousElementSibling as HTMLElement
  const next = el.value!.nextElementSibling as HTMLElement
  if (!prev || !next) return

  e.preventDefault()
  el.value!.setPointerCapture(e.pointerId)
  dragging.value = true

  // Disable transitions on siblings so dragging feels instant
  const prevTransition = prev.style.transition
  const nextTransition = next.style.transition
  prev.style.transition = 'none'
  next.style.transition = 'none'

  const startPos = isH.value ? e.clientX : e.clientY
  const prevStart = isH.value ? prev.offsetWidth : prev.offsetHeight
  const nextStart = isH.value ? next.offsetWidth : next.offsetHeight

  function onMove(e: PointerEvent) {
    const delta = (isH.value ? e.clientX : e.clientY) - startPos
    if (prevStart + delta < props.minSize || nextStart - delta < props.minSize) return
    if (props.maxSize !== undefined && nextStart - delta > props.maxSize) return
    prev.style.flexBasis = `${prevStart + delta}px`
    next.style.flexBasis = `${nextStart - delta}px`
    prev.style.flexGrow = '0'
    next.style.flexGrow = '0'
  }

  function onUp() {
    dragging.value = false
    prev.style.transition = prevTransition
    next.style.transition = nextTransition
    el.value!.removeEventListener('pointermove', onMove)
    el.value!.removeEventListener('pointerup', onUp)
    emit('resize',
      isH.value ? prev.offsetWidth : prev.offsetHeight,
      isH.value ? next.offsetWidth : next.offsetHeight,
    )
  }

  el.value!.addEventListener('pointermove', onMove)
  el.value!.addEventListener('pointerup', onUp)
}
</script>

<template>
  <!-- Flush: the root is the 1px line (`sidebar-border`: 70% in light, opaque in dark, where the
       planes are only ~1.16:1 apart); the hit area is a 12px child centred over it, outside the
       root's layout box, so the neighbours sit against the line with no gap. Pointer events on the
       child bubble to the root's handler, which owns the capture. -->
  <div
    v-if="flush"
    ref="el"
    role="separator"
    aria-orientation="vertical"
    :class="[
      'shrink-0 select-none touch-none relative w-px transition-colors bg-sidebar-border/70 dark:bg-sidebar-border hover:bg-primary/30',
      dragging && '!bg-primary/40',
    ]"
    @pointerdown="onPointerDown"
  >
    <div class="absolute inset-y-0 -left-1.5 -right-1.5 z-10 cursor-col-resize" />
  </div>
  <div
    v-else
    ref="el"
    role="separator"
    :aria-orientation="isH ? 'vertical' : 'horizontal'"
    :class="[
      'shrink-0 select-none touch-none relative group',
      isH ? 'w-2 cursor-col-resize' : 'h-2 cursor-row-resize',
    ]"
    @pointerdown="onPointerDown"
  >
    <div
      :class="[
        'absolute transition-colors',
        isH
          ? 'top-0 bottom-0 left-1/2 -translate-x-1/2 w-px bg-border group-hover:bg-primary/30'
          : 'left-0 right-0 top-1/2 -translate-y-1/2 h-px bg-border group-hover:bg-primary/30',
        dragging && '!bg-primary/40',
      ]"
    />
  </div>
</template>
